<script lang="ts">
import { ref } from 'vue';

import { uid } from '@sg-core/resources/assets/js/utils';

import PhxsgToast from './phxsg-toast.vue';

export type ToastType = 'info' | 'warning' | 'danger' | 'success';
export type ToastPlacement = 'top-start' | 'top-end' | 'bottom-end' | 'bottom-start';

export interface Toast {
  id: string;
  type: ToastType;
  text: string;
  duration: number;
  persistant: boolean;
}

export interface NotifyOptions {
  text?: string;
  type?: ToastType;
  persistant?: boolean;
  duration?: number;
  placement?: ToastPlacement;
}

export interface PhxsgToasterProps {
  closeText: string;
  type?: ToastType;
  duration?: number;
  placement?: ToastPlacement;
  transition?: string;
}

export interface ExposedToasterMethods {
  notify: (options: NotifyOptions) => void;
  close: (id?: string | number) => void;
  clear: () => void;
}
</script>

<script setup lang="ts">
const {
  type = 'info',
  duration = 8000,
  placement = 'top-end',
  transition = 'phxsg-toaster__item-',
} = defineProps<PhxsgToasterProps>();

const toasts = ref<Toast[]>([]);
const toastPlacement = ref<ToastPlacement>(placement);

const id = uid('toast');

function notify(toast: NotifyOptions): void {
  if (!toast?.text) {
    return;
  }

  const newToast: Toast = {
    id: `toast-${id}`,
    type: toast.type || type,
    persistant: toast.persistant === true,
    text: toast.text,
    duration: toast.duration || duration,
  };

  if (toast.placement) {
    toastPlacement.value = toast.placement;
  }

  /** Add the toast */
  toasts.value.unshift(newToast);
}

function close(id?: string | number): void {
  if (!id) return;
  toasts.value = toasts.value.filter((toast) => toast.id !== String(id));
}

function clear(): void {
  toasts.value = [];
}

defineExpose<ExposedToasterMethods>({ notify, close, clear });
</script>

<template>
  <TransitionGroup
    :class="['phxsg-toaster', `phxsg-toaster--${toastPlacement}`]"
    :name="transition"
    tag="ul"
    role="presentation"
    aria-live="polite"
    aria-atomic="true"
  >
    <PhxsgToast v-for="toast in toasts" v-bind="toast" :key="toast.id" :close-text="closeText" @close="close" />
  </TransitionGroup>
</template>

<style src="./toaster.css" />
